import { type RootState, type AppDispatch } from "features/Application/globaltypes/redux";
import { type FormikProps, withFormik } from "formik";
import { connect, type ConnectedProps } from "react-redux";
import { withAutosave, type AutosaveProps } from "utils/withAutosave";
import { type LandingPageFormFields, type LandingPageProps } from "./types";
import { bindAction } from "interfaces/redux";
import validationSchemas from "utils/validationSchemas";
import { noop } from "lodash";
import { useEffect, useRef } from "react";
import { type LandingPagePayload } from "../types/models";
import { campaignsStateSelector, landingPageFormFieldsSelector } from "../state/selectors";
import { saveLandingPage } from "../state/slices/landingPageSlice";
import LandingPageForm from "./Forms/LandingPageForm";
import { updateLandingPage } from "../state/thunks/campaignEntityStateThunk";

export type LandingPagePropsAll = LandingPageProps &
  FormikProps<LandingPageFormFields> &
  PropsFromRedux &
  AutosaveProps;

const LandingPage = (props: LandingPagePropsAll) => {
  const values = useRef(props.values);
  useEffect(() => {
    values.current = props.values;
  }, [props.values]);

  const saveLandingPage = () => props.saveLandingPage(values.current);

  useEffect(() => {
    props.acceptHandlers?.({
      onNext: saveLandingPage,
      onPrevious: saveLandingPage,
    });
    props.subscribeOnDiscarded?.(props.resetForm);
    return () => {
      props.unSubscribeOnDiscarded?.(props.resetForm);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <LandingPageForm {...props} campaignId={props.id} />;
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const base = campaignsStateSelector(state).base;

  return {
    id: base.entityState.entityId,
    isCreating: false,
    landingPageFormFields: landingPageFormFieldsSelector(state),
    isLoaded: base.landingPage.isLoaded,
    isLoading: base.landingPage.isLoading,
    isDraft: base.details.campaign.isDraft,
    validation: base.campaignValidation,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  updateLandingPage: bindAction(updateLandingPage, dispatch),
  saveLandingPage: bindAction(saveLandingPage, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

/* istanbul ignore next */
const withAutosaveComponent = withAutosave<LandingPagePropsAll, LandingPageFormFields, LandingPagePayload>({
  getInitValues: (props) => props.landingPageFormFields,
  stateProvider: (values) => values,
  entityUpdater: (props) => props.updateLandingPage,
})(LandingPage);

/* istanbul ignore next */
const component = withFormik({
  validationSchema: validationSchemas.landingPage,
  enableReinitialize: true,
  mapPropsToValues: (props: LandingPageProps & PropsFromRedux): LandingPageFormFields => props.landingPageFormFields,
  validateOnMount: true,
  handleSubmit: noop,
})(withAutosaveComponent);

const ConnectedComponent = connector(component);
export default ConnectedComponent;
